/**
 * Mailing profiles API (profiles mode).
 *
 * A profile pins one sending account + its own template over a subset of the
 * shared chat pool. Adding chats queues a background join for that account.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export type JoinStatus =
  | "pending"
  | "joined"
  | "manual_required"
  | "failed"
  | "not_needed";

export interface ProfileChat {
  chat_id: number;
  title: string;
  chat_ref: string;
  join_status: JoinStatus;
  join_error: string | null;
  joined_at: string | null;
}

export interface Profile {
  id: number;
  account_id: number;
  account_name: string | null;
  template_id: number | null;
  template_name: string | null;
  name: string;
  is_enabled: boolean;
  sort_order: number;
  interval_minutes: number;
  per_chat_delay_seconds: number;
  chats: ProfileChat[];
  created_at: string;
}

export interface ProfileCreate {
  account_id: number;
  template_id?: number | null;
  name?: string;
  chat_ids?: number[];
  interval_minutes?: number;
  per_chat_delay_seconds?: number;
}

export interface ProfilePatch {
  name?: string;
  template_id?: number | null;
  is_enabled?: boolean;
  interval_minutes?: number;
  per_chat_delay_seconds?: number;
}

// ============================================================
// Queries
// ============================================================

export function useProfiles(opts?: { refetchInterval?: number }) {
  return useQuery<Profile[], ApiError>({
    queryKey: ["profiles"],
    queryFn: () => api.get<Profile[]>("/profiles"),
    // Poll while joins are pending so join_status updates surface without a manual refresh.
    refetchInterval: opts?.refetchInterval ?? 8_000,
    staleTime: 4_000,
  });
}

// ============================================================
// Mutations
// ============================================================

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation<Profile, ApiError, ProfileCreate>({
    mutationFn: (body) => api.post<Profile>("/profiles", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation<Profile, ApiError, { id: number; patch: ProfilePatch }>({
    mutationFn: ({ id, patch }) => api.patch<Profile>(`/profiles/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
}

export function useAddProfileChats() {
  const qc = useQueryClient();
  return useMutation<Profile, ApiError, { id: number; chat_ids: number[] }>({
    mutationFn: ({ id, chat_ids }) =>
      api.post<Profile>(`/profiles/${id}/chats`, { chat_ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
}

export function useRemoveProfileChat() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { id: number; chat_id: number }>({
    mutationFn: ({ id, chat_id }) =>
      api.delete<void>(`/profiles/${id}/chats/${chat_id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
}

export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api.delete<void>(`/profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
}
